import React from "react";
import { Box, Text } from "@salestrak/ui";

import type { EntityVisualSize } from "./entity-visual";
import type { Omittable } from "@salestrak/ui";

const LABEL_TEXT_SIZE: Record<EntityVisualSize, "2xs" | "xs" | "sm" | "base" | "lg"> = {
  "2xs": "2xs",
  xs: "xs",
  sm: "sm",
  md: "base",
  lg: "base",
  xl: "lg",
};

/** The two quiet rungs of the text ramp a chip label can sit on. */
export type EntityLabelInk = "muted" | "faint";

export type EntityTagBodyProps = Omittable<{
  label: string;
  visual?: React.ReactNode;
  display: "full" | "visual" | "label";
  weight: "regular" | "medium" | "semibold";
  size: EntityVisualSize;
  subtitle?: string | null;
  /** Stack the subtitle BELOW the label (identity-card layout) instead of
   *  inline after it. */
  subtitleStacked?: boolean;
  /** Secondary line(s) rendered BELOW the label (stacked), e.g. a search
   *  result's address. An array is one clamped line per entry. Distinct from
   *  `subtitle`, which renders inline. */
  description?: string | readonly string[] | null;
  /** Show each clipped description line in full on hover. Only for a chip
   *  with no preview card — two hover layers on one chip fight each other. */
  descriptionOverflowTooltip?: boolean;
  /** A KIND word set ABOVE the label as a 10px tracked-caps line in faint
   *  ink — a role over a member's name. Typography does the separating: the
   *  kindLabel is the smallest, quietest line and sits on the other side of
   *  the name from the description, so it can never be read as a second
   *  description. */
  kindLabel?: string | null;
  /** Muted context rendered inline AFTER the label, on the label line — a
   *  search row's `in <location> · 3d ago`. Composes with `description`
   *  (which stays the stacked second line). */
  meta?: string | null;
  /** The quiet rung the label reads at: `muted` for a de-emphasized value,
   *  `faint` for a row the eye should pass over (a read inbox row). Unset =
   *  body ink. */
  labelInk?: EntityLabelInk;
  /** Line height for a box label. Use a roomier line inside a fixed control
   *  when the compact line box clips the active font's descenders. */
  labelLeading?: "none" | "tight" | "normal";
  /** `"text"` = the chip sits inline inside a sentence Text (see EntityTag). */
  as?: "box" | "text";
}>;

// The chip's "regular" weight maps to the text scale's "normal".
const TEXT_WEIGHT: Record<EntityTagBodyProps["weight"], "normal" | "medium" | "semibold"> = {
  regular: "normal",
  medium: "medium",
  semibold: "semibold",
};

function EntityTagBodyComponent({
  label,
  visual,
  display,
  weight,
  size,
  subtitle,
  subtitleStacked,
  description,
  descriptionOverflowTooltip,
  kindLabel,
  meta,
  labelInk,
  labelLeading = "none",
  as = "box",
}: EntityTagBodyProps) {
  if (display === "visual") return <>{visual}</>;

  // Inline label (as="text") flows within a wrapping sentence, so it must stay
  // an inline span: `truncate` maps to a block line-clamp that would force a
  // line break before and after the chip, and a fixed leading would desync its
  // line box from the surrounding text.
  const inline = as === "text";

  const labelText = (
    <Text
      size={LABEL_TEXT_SIZE[size]}
      weight={TEXT_WEIGHT[weight]}
      variant={labelInk}
      truncate={inline ? undefined : 1}
      // Pin line box to glyph height so the shell's ghost-wash bleed (fixed
      // negative margin) lands predictably and the chip height matches the text.
      leading={inline ? undefined : labelLeading}
    >
      {label}
    </Text>
  );

  if (display === "label") return labelText;

  // Baseline-aligned with the label so the pair reads as one line of text;
  // the label truncates first (it carries its own clamp), the meta clamps
  // independently rather than pushing the row wider.
  const metaText = meta ? (
    <Text
      size={LABEL_TEXT_SIZE[size]}
      variant="muted"
      truncate={1}
      leading="none"
    >
      {meta}
    </Text>
  ) : null;
  const labelLine = metaText ? (
    // xs (4px), not 2xs — the label's clamp box swallows its trailing
    // side-bearing, so at 2px the meta reads glued to the name.
    <Box shrink direction="row" align="baseline" gap="xs" minWidth={0}>
      {labelText}
      {metaText}
    </Box>
  ) : (
    labelText
  );

  if (subtitle && subtitleStacked) {
    return (
      <>
        {visual}
        {/* Stacked two-line card = a fixed 14px/12px type ramp — `size` keeps
         * sizing the avatar only, so a larger glyph never inflates the text. */}
        <Box shrink direction="column" gap="2xs" minWidth={0}>
          <Text
            size="sm"
            weight={TEXT_WEIGHT[weight]}
            variant={labelInk}
            truncate={1}
            leading="none"
          >
            {label}
          </Text>
          <Text size="xs" variant="muted" truncate={1} leading="none">
            {subtitle}
          </Text>
        </Box>
      </>
    );
  }

  const descriptionLines = (
    typeof description === "string" ? [description] : (description ?? [])
  ).filter((line) => line.length > 0);
  if (descriptionLines.length > 0 || kindLabel) {
    return (
      <>
        {visual}
        <Box shrink direction="column" gap="2xs" minWidth={0}>
          {kindLabel ? (
            <Text
              caps
              size="2xs"
              variant="faint"
              weight="medium"
              truncate={1}
              leading="none"
              overflowTooltip={descriptionOverflowTooltip}
            >
              {kindLabel}
            </Text>
          ) : null}
          {labelLine}
          {descriptionLines.map((line, index) => (
            <Text
              // Lines are positional and can repeat.
              key={index}
              size="xs"
              variant="muted"
              truncate={1}
              leading="none"
              overflowTooltip={descriptionOverflowTooltip}
            >
              {line}
            </Text>
          ))}
        </Box>
      </>
    );
  }

  if (subtitle) {
    return (
      <>
        {visual}
        <Box shrink direction="row" align="baseline" gap="2xs">
          {labelText}
          <Text
            size={LABEL_TEXT_SIZE[size]}
            variant="muted"
            truncate={1}
            leading="none"
          >
            {`· ${subtitle}`}
          </Text>
        </Box>
      </>
    );
  }

  return (
    <>
      {visual}
      <Box shrink direction="row" align="center" minWidth={0}>
        {labelLine}
      </Box>
    </>
  );
}

EntityTagBodyComponent.displayName = "EntityTagBody";

export const EntityTagBody = React.memo(EntityTagBodyComponent);
